import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCase } from '../../../hooks';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';
import { useQuery } from 'hooks/useQuery';
import { isPathEqualToRouteName } from 'utils/route';
import { ERouteLinks } from 'models/route';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { Grid } from '@mui/material';
import { StyledPageTitle } from 'theme/styles';
import { Loader } from 'components/atoms/Loader';
import { INoteFormValue } from 'models/cases';
import FormNote from './formNote';

const AddEditNotePage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const {
    id = '',
    caseId = '',
    noteId = '',
  } = useParams<{ id: string; caseId: string; noteId: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { pathname }: any = useLocation();
  const isFromLE = isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityCaseNotePage);
  const isEdit =
    isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityCaseNoteEditPage) ||
    isPathEqualToRouteName(pathname, ERouteLinks.CaseNoteEditPage);
  const { getNoteData, note, updateNote, addNote } = useCase();
  const [initialValues, setInitialValues] = useState<INoteFormValue | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const breadcrumbs = getBreadcrumbsBasic(isFromLE, type, t, false, id);

  useEffect(() => {
    (async () => {
      if (isEdit) {
        setIsLoading(true);
        await getNoteData(noteId);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (note) {
      setInitialValues({ name: note.name, description: note.description });
    }
  }, [note]);

  const backLink = isFromLE
    ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
    : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`;

  const onSubmit = async (data: INoteFormValue) => {
    let res = null;
    if (isEdit && noteId) {
      res = await updateNote(caseId, noteId, data);
    } else if (!isEdit) {
      res = await addNote(caseId, data);
    }
    if (res) {
      navigate(backLink);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          ...breadcrumbs,
          {
            label: t(Elabels.caseDetailsHeader),
            route: isFromLE
              ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
              : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
          },
          t(Elabels.caseNoteAddHeader),
        ]}
      />
      <Grid sx={{ alignItems: 'center' }}>
        <StyledPageTitle variant="h4">{t(Elabels.caseNoteAddHeader)}</StyledPageTitle>
      </Grid>
      {isLoading && (
        <Grid size={{ xs: 12 }}>
          <Loader isLoading />
        </Grid>
      )}
      <Grid container>
        {!isLoading && (
          <Grid size={{ xs: 12 }}>
            <FormNote
              isEdit={isEdit}
              initialValues={initialValues}
              onSubmit={onSubmit}
              backLink={backLink}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default AddEditNotePage;
